import { useQuery } from '@tanstack/react-query'
import { Plus, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Can } from '@/features/auth/guards'
import { departmentsQuery, designationsQuery, staffListQuery } from '@/features/staff/api'
import { staffStatusTone } from '@/features/staff/status'
import { EMPLOYMENT_TYPES, STAFF_STATUSES, type StaffMember } from '@/features/staff/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { formatDate, humanize } from '@/utils/format'

const FILTERS = ['departmentId', 'designationId', 'status', 'employmentType'] as const

export function StaffListPage() {
  const { t } = useTranslation('staff')
  useDocumentTitle('Staff')
  const navigate = useNavigate()
  const { state, query, update, toggleSort, setPage } = useListParams(FILTERS, { sort: 'name' })
  const list = useQuery(staffListQuery(query))
  const departments = useQuery(departmentsQuery())
  const designations = useQuery(designationsQuery())

  const columns: Column<StaffMember>[] = [
    {
      key: 'name',
      header: t('Staff member'),
      sortKey: 'name',
      cell: (m) => (
        <div className="flex items-center gap-3">
          <Avatar name={m.fullName} src={m.photoUrl} size="sm" />
          <div className="min-w-0">
            <Link
              to={`/admin/staff/${m.id}`}
              onClick={(e) => e.stopPropagation()}
              className="block truncate font-medium text-ink hover:text-brand-700"
            >
              {m.fullName}
            </Link>
            <span className="block truncate text-xs text-muted">{m.email ?? m.employeeCode}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'code',
      header: t('Employee ID'),
      sortKey: 'employeeCode',
      hideBelow: 'lg',
      cell: (m) => <span className="font-mono text-xs">{m.employeeCode}</span>,
    },
    {
      key: 'designation',
      header: t('Designation'),
      sortKey: 'designation',
      cell: (m) => m.designation?.name ?? '—',
    },
    {
      key: 'department',
      header: t('Department'),
      sortKey: 'department',
      hideBelow: 'md',
      cell: (m) => m.department?.name ?? '—',
    },
    { key: 'type', header: t('Employment'), hideBelow: 'xl', cell: (m) => humanize(m.employmentType) },
    {
      key: 'joined',
      header: t('Joined'),
      sortKey: 'joiningDate',
      hideBelow: 'xl',
      cell: (m) => formatDate(m.joiningDate),
    },
    {
      key: 'status',
      header: t('Status'),
      sortKey: 'status',
      cell: (m) => (
        <Badge tone={staffStatusTone[m.status]} dot size="sm">
          {humanize(m.status)}
        </Badge>
      ),
    },
  ]

  const hasFilters = Boolean(state.search || Object.keys(state.filters).length)

  return (
    <>
      <PageHeader
        title={t('Staff')}
        description={t('Teachers and support staff, their roles and employment details.')}
        breadcrumbs={[{ label: t('People') }, { label: t('Staff') }]}
        homeHref="/admin"
        actions={
          <Can permission="staff.manage">
            <Button asChild leftIcon={<Plus />}>
              <Link to="/admin/staff/new">{t('Hire staff')}</Link>
            </Button>
          </Can>
        }
      />
      <Card>
        <FilterBar
          actions={
            hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  update({
                    search: undefined,
                    departmentId: undefined,
                    designationId: undefined,
                    status: undefined,
                    employmentType: undefined,
                  })
                }
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Name, employee ID, email or phone')}
            label={t('Search staff')}
          />
          <Select
            aria-label={t('Filter by department')}
            className="sm:w-auto sm:min-w-48"
            placeholder={t('All departments')}
            value={state.filters.departmentId ?? ''}
            onChange={(e) => update({ departmentId: e.target.value })}
            options={(departments.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
          />
          <Select
            aria-label={t('Filter by designation')}
            className="sm:w-auto sm:min-w-44"
            placeholder={t('All designations')}
            value={state.filters.designationId ?? ''}
            onChange={(e) => update({ designationId: e.target.value })}
            options={(designations.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
          />
          <Select
            aria-label={t('Filter by status')}
            className="sm:w-auto sm:min-w-36"
            placeholder={t('Any status')}
            value={state.filters.status ?? ''}
            onChange={(e) => update({ status: e.target.value })}
            options={STAFF_STATUSES.map((s) => ({ value: s, label: humanize(s) }))}
          />
          <Select
            aria-label={t('Filter by employment type')}
            className="sm:w-auto sm:min-w-40"
            placeholder={t('Any type')}
            value={state.filters.employmentType ?? ''}
            onChange={(e) => update({ employmentType: e.target.value })}
            options={EMPLOYMENT_TYPES.map((s) => ({ value: s, label: humanize(s) }))}
          />
        </FilterBar>
        <DataTable
          caption={t('Staff')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(m) => m.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          sort={{ key: state.sort, direction: state.direction }}
          onSortChange={toggleSort}
          onRowClick={(m) => void navigate(`/admin/staff/${m.id}`)}
          mobileCard={(m) => (
            <div className="flex items-center gap-3">
              <Avatar name={m.fullName} src={m.photoUrl} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{m.fullName}</p>
                <p className="truncate text-xs text-muted">
                  {[m.designation?.name, m.department?.name].filter(Boolean).join(' · ') || m.employeeCode}
                </p>
              </div>
              <Badge tone={staffStatusTone[m.status]} size="sm">
                {humanize(m.status)}
              </Badge>
            </div>
          )}
          empty={
            <EmptyState
              icon={<UserRound />}
              title={hasFilters ? t('No staff match these filters') : t('No staff yet')}
              description={hasFilters ? t('Try a different search.') : t('Hire your first staff member.')}
              compact
            />
          }
        />
        <Pagination
          meta={list.data?.meta}
          onPageChange={setPage}
          onPerPageChange={(n) => update({ perPage: n })}
        />
      </Card>
    </>
  )
}
